import React from 'react'
import styled from 'styled-components'
import {colors} from '../../sharedComponents/colors'
import {minutesToDurationString} from '../../Components/Atoms/Utils'
import {Alarm} from '../../state/alarms/state'
import {alarmNameFormatter, dateValueFormatter, limitFormatter, Units} from './formatters'
import {TFunction} from 'i18next'
import {EMPTY_VALUE_TEXT, Text} from '../../sharedComponents/components'
import {SmartAsset} from '../../Components/Atoms/ImageLoader'
import {useAppState} from '../../state'

const ResolvedAlarmExpandedRowContainer = styled.div`
  display: grid;
  margin: 1rem 0;
  grid-template-columns: 4% 56% 28%;
  grid-template-rows: 3.5rem auto;

  grid-template-areas:
    '. reason   resolvedTime  limit'
    '. note     duration      target';
`

const TitleValueLabelColumn = ({title, value}: {title: string; value: string}) => (
  <div style={{display: 'flex', flexDirection: 'column'}}>
    <Text size="XS" color={colors.system.grey_50}>
      {title}
    </Text>
    <Text size="S">{value}</Text>
  </div>
)

const AssetThumb = styled.div`
  width: 150px;
  height: 125px;
  flex: 0 0 auto;
  position: relative;
  border: 1px solid #000;
  border-radius: 8px;
  overflow: hidden;
  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    display: block;
  }
`

interface ResolvedAlarmExpandedRowProps {
  t: TFunction
  alarm: Alarm
  locale: string
  units: Units
}

export const ResolvedAlarmExpandedRow: React.FC<ResolvedAlarmExpandedRowProps> = ({t, alarm, locale, units}) => {
  const {actions} = useAppState()

  const openAssetInNewTab = async (assetId: string) => {
    try {
      const d = await actions.getDownloadUrl(assetId)
      if (d && d.downloadURL) {
        window.open(d.downloadURL, '_blank', 'noopener')
      }
    } catch (err) {
      console.error('Error opening asset', err)
    }
  }

  return (
    <ResolvedAlarmExpandedRowContainer>
      <div style={{gridArea: 'reason'}}>
        <TitleValueLabelColumn title={`${t('alarms:label.details.reason', 'Reason')}`} value={alarm.reason} />
      </div>
      <div style={{gridArea: 'note'}}>
        <TitleValueLabelColumn
          title={`${t('alarms:label.details.note', 'Note')}`}
          value={alarm.note || EMPTY_VALUE_TEXT}
        />
      </div>
      <div style={{gridArea: 'duration'}}>
        <TitleValueLabelColumn
          title={`${t('alarms:label.details.duration', 'Duration')}`}
          value={!!alarm.duration ? minutesToDurationString(alarm.duration, true) : EMPTY_VALUE_TEXT}
        />
      </div>
      <div style={{gridArea: 'resolvedTime'}}>
        <TitleValueLabelColumn
          title={`${t('alarms:label.details.resolvedTime', 'Resolved time')}`}
          value={dateValueFormatter(alarm.resolvedTime, locale)}
        />
      </div>
      {(alarm.type as any) !== 'calibration' ? (
        <>
          <div style={{gridArea: 'limit'}}>
            <TitleValueLabelColumn
              title={`${t('alarms:label.details.limit', 'Limit')}`}
              value={limitFormatter(t, alarm, units)}
            />
          </div>
          {alarm.assets && alarm.assets.length > 0 ? (
            <>
              <div style={{gridColumn: '2 / span 2'}}>
                <Text size="XS" color={colors.system.grey_50}>
                  {`${t('alarms:label.details.image', 'Image')}`}
                </Text>
              </div>
              <div style={{gridColumn: '2 / span 2', display: 'flex', gap: '0.5rem', alignItems: 'center'}}>
                <div style={{display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'flex-start'}}>
                  {alarm.assets.map(a => (
                    <AssetThumb
                      key={a.id}
                      title={a.name}
                      onClick={() => openAssetInNewTab(a.id)}
                      style={{cursor: 'pointer'}}
                    >
                      <SmartAsset asset={a as any} />
                    </AssetThumb>
                  ))}
                </div>
              </div>
            </>
          ) : null}
        </>
      ) : (
        <div style={{gridArea: 'limit'}}>
          <TitleValueLabelColumn
            title={`${t('alarms:label.details.targetDay', 'Target day')}`}
            value={dateValueFormatter(alarm.targetDay, locale)}
          />
        </div>
      )}
      {alarm.measurementTarget ? (
        <div style={{gridArea: 'target'}}>
          <TitleValueLabelColumn
            title={`${t('tasks:labels.measurementValueTarget', 'Measurement target')}`}
            value={alarmNameFormatter(t, alarm.measurementTarget, null)}
          />
        </div>
      ) : (
        <></>
      )}
    </ResolvedAlarmExpandedRowContainer>
  )
}
